import { useMemo } from 'react';
import { DEFAULT_VIEW_CHUNK_LIMIT } from 'src/graphql/entities/custom-view/constants';
import { prepareSortAttributes } from 'src/graphql/entities/custom-view/custom-view-config/utils';
import { CustomViewSorting } from 'src/graphql/entities/custom-view/custom-view.types';
import { useSearch } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import useDebouncedValue from 'src/utils/hooks/use-debounced-value';
import { ProjectAttributeAccessors, ProjectAttributeType } from './constants';

// this hook abstracts the interface required for generic custom views table
export const useProjectRecords = ({
  filters: rawFilters,
  sort,
  limit = DEFAULT_VIEW_CHUNK_LIMIT,
  searchTerm,
  dynamicAttributePaths,
}: {
  filters: ConditionInput[];
  sort?: CustomViewSorting;
  limit?: number;
  searchTerm?: string;
  dynamicAttributePaths?: string[];
}) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const filter = useDebouncedValue(rawFilters, 500);

  const options = useMemo(() => {
    return {
      searchTerm: searchTerm ?? '',
      filter,
      limit,
      partnerId: selectedPartnerId,
      sort: prepareSortAttributes<ProjectAttributeType>(sort, ProjectAttributeAccessors),
      entityName: EntityNames.Project,
      showResultsWhenEmptySearchTerm: true,
      dynamicAttributePaths,
    };
  }, [filter, limit, selectedPartnerId, sort, searchTerm, dynamicAttributePaths]);

  const data = useSearch(options);

  if (!data) {
    throw new Error('Search is not configured to search for projects');
  }

  return data;
};
